import React, { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { get, isEmpty } from 'lodash';
import {
  appServiceRawObjectFields,
  appServiceRawObjectTypes,
  fetchObjectTypesFields,
  fetchTestObjectTypesFields,
  updateObjectExtIdForObjectType,
  updateObjectTypeFields,
} from 'slices/appService.slice';
import { useAppDispatch } from 'slices/store';
import CONSTANTS from '@timeedit/types/lib/constants';
import { EFieldMappingModes } from '@timeedit/types/lib/enums';
import { Col, Form, Row, Select } from '@timeedit/ui-components/antd';

type MappingSelectorType = {
  appName?: any;
  objectType?: any;
  objectTypeIdx?: any;
  allowedMappingModes?: any;
  rawObjectFields?: any;
  rawObjectTypesLoading?: any;
  allowDynamicObjectTypes?: any;
};

const MappingSelector = ({
  appName,
  objectType,
  objectTypeIdx,
  allowedMappingModes = [
    EFieldMappingModes.MAPPED,
    EFieldMappingModes.FREE_MAPPING,
  ],
  rawObjectFields = [],
  rawObjectTypesLoading = false,
  allowDynamicObjectTypes,
}: MappingSelectorType) => {
  const dispatch = useAppDispatch();
  const rawObjectTypes = useSelector(appServiceRawObjectTypes(appName));
  // Memoized vars
  const { objectTypeExtId, fieldMappingMode, applicationObjectTypeName } =
    useMemo(() => objectType, [objectType]);
  const dataField = useSelector(
    appServiceRawObjectFields(appName, objectTypeExtId)
  );

  useEffect(() => {
    if (!rawObjectFields[objectTypeExtId] && objectTypeExtId) {
      dispatch(fetchObjectTypesFields({ appName, type: objectTypeExtId }));
    }
  }, [objectType?.objectTypeExtId]);
  useEffect(() => {
    if (objectTypeExtId)
      dispatch(
        fetchTestObjectTypesFields({
          appName,
          type: applicationObjectTypeName,
          objectTypeExtId,
        })
      );
  }, []);

  useEffect(() => {
    if (!isEmpty(dataField) && appName !== 'teExam') {
      dispatch(
        updateObjectTypeFields({
          appName,
          dataField,
          objectTypeExtId,
        })
      );
    }
  }, [dataField, objectTypeExtId]);

  const onUpdateObjectExtIdForObjectType = useCallback(
    (objectTypeExtId: any) => {
      // Only call api to get new rawObjectFields
      dispatch(
        updateObjectExtIdForObjectType({
          appName,
          objectTypeIdx,
          objectTypeExtId,
        })
      );
      if (objectTypeExtId) {
        dispatch(
          fetchTestObjectTypesFields({
            appName,
            type: objectTypeExtId,
            objectTypeExtId,
          })
        );
      }
    },
    [appName, objectTypeIdx, !!rawObjectFields[objectTypeExtId]]
  );

  return (
    <Row gutter={16}>
      <Col md={12}>
        <Form.Item label="Mapping type" className="data-model">
          <Select
            value={fieldMappingMode || undefined}
            style={{ minWidth: '100%' }}
            // onChange={onUpdateFieldMappingModeForObjectType}
          >
            {Object.keys(EFieldMappingModes)
              .filter((el) => allowedMappingModes.indexOf(el) > -1)
              .map((el) => (
                <Select.Option key={el} value={el}>
                  {get(CONSTANTS, `FieldMappingModeProps.${el}.label`)}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
      </Col>
      <Col md={12}>
        <Form.Item label="Mapped to object type" className="data-model">
          <Select
            placeholder="No object type selected"
            value={objectTypeExtId || undefined}
            style={{ minWidth: '100%' }}
            loading={rawObjectTypesLoading}
            disabled={allowDynamicObjectTypes || rawObjectTypesLoading}
            showSearch
            onChange={onUpdateObjectExtIdForObjectType}
            allowClear
            filterOption={(query: any, item: any) =>
              !query ||
              `${item.props.value}${item.props.children}`
                .toLowerCase()
                .indexOf(query.toLowerCase()) > -1
            }
          >
            {rawObjectTypes &&
              rawObjectTypes.map((item: any) => (
                <Select.Option value={item.extid} key={item.extid}>
                  {item.name}
                </Select.Option>
              ))}
          </Select>
        </Form.Item>
      </Col>
    </Row>
  );
};

export default React.memo(MappingSelector);
